import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  People as GroupIcon,
  Person as PersonIcon,
  Forum as FamilyIcon,
  PushPin as PinIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { getConversationDisplayName, getUserConversationMeta } from '../../services/messagingService';
import logger from '../../utils/logger';

/**
 * Conversation list sidebar — shows all conversations sorted by recency.
 * Pinned conversations appear first, then sorted by last message time.
 */
const ConversationList = ({
  conversations,
  activeConversationId,
  onSelect,
  currentUserId,
  memberMap,
  familyCode,
  loading
}) => {
  const [search, setSearch] = useState('');
  const [userMeta, setUserMeta] = useState({ conversations: {}, totalUnread: 0 });

  // Load user's per-conversation metadata (unread counts, pins, mutes)
  useEffect(() => {
    if (!familyCode || !currentUserId) return;

    const loadMeta = async () => {
      try {
        const meta = await getUserConversationMeta(familyCode, currentUserId);
        setUserMeta(meta);
      } catch (err) {
        logger.warn('Error loading user conversation meta:', err);
      }
    };

    loadMeta();
  }, [familyCode, currentUserId, conversations]);

  // Filter conversations by search
  const filteredConversations = conversations.filter((conv) => {
    if (!search.trim()) return true;
    const name = getConversationDisplayName(conv, currentUserId, memberMap);
    return name.toLowerCase().includes(search.toLowerCase());
  });

  // Sort: pinned first, then by updatedAt
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const aPinned = userMeta.conversations?.[a.id]?.isPinned || false;
    const bPinned = userMeta.conversations?.[b.id]?.isPinned || false;

    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;

    const aTime = a.updatedAt?.toDate?.() || a.updatedAt || 0;
    const bTime = b.updatedAt?.toDate?.() || b.updatedAt || 0;
    return bTime - aTime;
  });

  const getConversationIcon = (type) => {
    if (type === 'family') return <FamilyIcon />;
    if (type === 'group') return <GroupIcon />;
    return <PersonIcon />;
  };

  const getAvatarColor = (type) => {
    if (type === 'family') return 'success.main';
    if (type === 'group') return 'info.main';
    return 'primary.main';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            )
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
      </Box>

      <Divider />

      {/* Conversation list */}
      <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
        {sortedConversations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {search ? 'No conversations match your search' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          sortedConversations.map((conv) => {
            const displayName = getConversationDisplayName(conv, currentUserId, memberMap);
            const unread = userMeta.conversations?.[conv.id]?.unreadCount || 0;
            const isPinned = userMeta.conversations?.[conv.id]?.isPinned || false;
            const isActive = conv.id === activeConversationId;
            const lastMsg = conv.lastMessage;

            return (
              <ListItemButton
                key={conv.id}
                selected={isActive}
                onClick={() => onSelect(conv)}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.lighter',
                    borderRight: 3,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <ListItemAvatar>
                  <Badge badgeContent={unread} color="error" overlap="circular">
                    <Avatar sx={{ bgcolor: getAvatarColor(conv.type), width: 44, height: 44 }}>
                      {getConversationIcon(conv.type)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          flex: 1,
                          fontWeight: unread > 0 ? 700 : 400
                        }}
                      >
                        {displayName}
                      </Typography>
                      {isPinned && <PinIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                      {lastMsg?.timestamp && (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatTime(lastMsg.timestamp)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    lastMsg ? (
                      <Typography
                        variant="body2"
                        color={unread > 0 ? 'text.primary' : 'text.secondary'}
                        noWrap
                        sx={{ fontWeight: unread > 0 ? 600 : 400 }}
                      >
                        {lastMsg.authorName}: {lastMsg.text}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled" fontStyle="italic">
                        No messages yet
                      </Typography>
                    )
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>
    </Box>
  );
};

export default ConversationList;
